export const calculateTrendData = (expenses = [], funds = []) => {
  if (!expenses?.length && !funds?.length) {
    return {
      inflows: [0, 0], // Provide at least two points for a valid line
      outflows: [0, 0],
      totalInflow: 0,
      totalOutflow: 0,
      dailyTotals: {}
    };
  }

  // Prepare all transactions with their types
  const allTransactions = [
    ...(expenses || []).map(expense => ({
      ...expense,
      isOutflow: true,
      amount: expense.grandTotal ? Number(expense.grandTotal) : Number(expense.amount) || 0,
      createdAt: expense.createdAt || new Date().toISOString()
    })),
    ...(funds || []).map(fund => ({
      ...fund,
      isOutflow: false,
      amount: Number(fund.amount || 0),
      createdAt: fund.createdAt || new Date().toISOString()
    }))
  ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  // If still no transactions after filtering, return safe defaults
  if (!allTransactions.length) {
    return {
      inflows: [0, 0],
      outflows: [0, 0],
      totalInflow: 0,
      totalOutflow: 0,
      dailyTotals: {}
    };
  }

  // Group transactions by date and calculate daily totals
  const dailyTotals = allTransactions.reduce((acc, transaction) => {
    const date = new Date(transaction.createdAt).toLocaleDateString();
    
    if (!acc[date]) {
      acc[date] = { inflow: 0, outflow: 0 };
    }
    
    if (transaction.isOutflow) {
      acc[date].outflow += Number(transaction.amount) || 0;
    } else {
      acc[date].inflow += Number(transaction.amount) || 0;
    }
    
    return acc;
  }, {});

  // If no daily totals were created, provide defaults
  if (Object.keys(dailyTotals).length === 0) {
    return {
      inflows: [0, 0],
      outflows: [0, 0],
      totalInflow: 0,
      totalOutflow: 0,
      dailyTotals: {}
    };
  }

  // Convert to arrays for the trend lines
  const sortedDates = Object.keys(dailyTotals).sort((a, b) => 
    new Date(a) - new Date(b)
  );
  
  const outflowData = sortedDates.map(date => dailyTotals[date].outflow);
  const inflowData = sortedDates.map(date => dailyTotals[date].inflow);

  if (outflowData.length < 2) outflowData.push(outflowData[0] || 0);
  if (inflowData.length < 2) inflowData.push(inflowData[0] || 0);

  const totalOutflow = outflowData.reduce((sum, val) => sum + (val || 0), 0);
  const totalInflow = inflowData.reduce((sum, val) => sum + (val || 0), 0);

  return {
    inflows: inflowData,
    outflows: outflowData,
    totalInflow,
    totalOutflow,
    dailyTotals
  };
};

export const calculateMonthlySummary = (expenses = [], funds = []) => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  
  // Filter transactions by month
  const currentMonthFunds = funds.filter(fund => {
    const date = new Date(fund.createdAt);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  });
  
  const previousMonthFunds = funds.filter(fund => {
    const date = new Date(fund.createdAt);
    return date.getMonth() === previousMonth && date.getFullYear() === previousYear;
  });
  
  const currentMonthExpenses = expenses.filter(expense => {
    const date = new Date(expense.createdAt);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  });
  
  const previousMonthExpenses = expenses.filter(expense => {
    const date = new Date(expense.createdAt);
    return date.getMonth() === previousMonth && date.getFullYear() === previousYear;
  });
  
  const currentMonthFundsTotal = currentMonthFunds.reduce((sum, fund) => sum + Number(fund.amount || 0), 0);
  const previousMonthFundsTotal = previousMonthFunds.reduce((sum, fund) => sum + Number(fund.amount || 0), 0);
  const currentMonthExpensesTotal = currentMonthExpenses.reduce((sum, expense) => 
    sum + Number(expense.grandTotal || expense.amount || 0), 0);
  const previousMonthExpensesTotal = previousMonthExpenses.reduce((sum, expense) => 
    sum + Number(expense.grandTotal || expense.amount || 0), 0);
  
  // Calculate percentage changes
  let fundsPercentChange = 0;
  if (previousMonthFundsTotal > 0) {
    if (currentMonthFundsTotal === 0) {
      // Funds dropped to zero (100% decrease)
      fundsPercentChange = -100;
    } else {
      fundsPercentChange = Math.round((currentMonthFundsTotal - previousMonthFundsTotal) / previousMonthFundsTotal * 100);
    }
  } else if (currentMonthFundsTotal > 0) {
    fundsPercentChange = 100; // New funds appeared
  }
  
  let expensesPercentChange = 0;
  if (previousMonthExpensesTotal > 0) {
    if (currentMonthExpensesTotal === 0) {
      // Expenses dropped to zero (100% decrease)
      expensesPercentChange = -100;
    } else {
      expensesPercentChange = Math.round((currentMonthExpensesTotal - previousMonthExpensesTotal) / previousMonthExpensesTotal * 100);
    }
  } else if (currentMonthExpensesTotal > 0) {
    expensesPercentChange = 100; // New expenses appeared
  }
  
  return {
    currentMonthFunds: currentMonthFundsTotal,
    previousMonthFunds: previousMonthFundsTotal,
    currentMonthExpenses: currentMonthExpensesTotal,
    previousMonthExpenses: previousMonthExpensesTotal,
    fundsPercentChange,
    expensesPercentChange
  };
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined) return "₦0";

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "₦0";

  // Negatives as -₦… (sign before the currency symbol).
  const sign = numeric < 0 ? "-" : "";
  const absValue = Math.abs(numeric);
  const withSymbol = (num) => `${sign}₦${num.toLocaleString("en-NG")}`;

  if (absValue >= 1000000000) {
    return withSymbol((absValue / 1000000000).toFixed(1)) + "B";
  }
  if (absValue >= 1000000) {
    return withSymbol((absValue / 1000000).toFixed(1)) + "M";
  }
  if (absValue >= 1000) {
    return withSymbol((absValue / 1000).toFixed(1)) + "K";
  }

  return withSymbol(absValue);
};

const percentChange = (current, previous) => {
  const curr = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev > 0) {
    if (curr === 0) return -100;
    return Math.round(((curr - prev) / prev) * 100);
  }
  if (curr > 0) return 100;
  return 0;
};

const parsePercentDiff = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/** Maps GET /v1/expense/total-expenses/{organizationID} into overview card fields. */
export const mapExpenseTotals = (totals) => {
  const data =
    totals?.data && typeof totals.data === "object" && !Array.isArray(totals.data)
      ? totals.data
      : totals && typeof totals === "object"
        ? totals
        : {};

  const totalInflows = Number(data.totalFunds ?? 0);
  const totalOutflows = Number(data.totalExpenses ?? 0);
  // Floor at 0 so the balance card never shows a deficit.
  const currentBalance = Math.max(0, totalInflows - totalOutflows);

  const currentMonthFunds = Number(data.thisMonthTotalCampaignFunds ?? 0);
  const previousMonthFunds = Number(data.lastMonthTotalCampaignFunds ?? 0);
  const currentMonthExpenses = Number(data.thisMonthTotalExpenses ?? 0);
  const previousMonthExpenses = Number(data.lastMonthTotalExpenses ?? 0);

  const fundsPercentChange = parsePercentDiff(
    data.campaignFundsPercentageDiff,
    percentChange(currentMonthFunds, previousMonthFunds)
  );
  const expensesPercentChange = parsePercentDiff(
    data.expensesPercentageDiff,
    percentChange(currentMonthExpenses, previousMonthExpenses)
  );

  return {
    totalInflows,
    totalOutflows,
    currentBalance,
    totalDonations: Number(data.totalDonations ?? 0),
    donationsPercentChange: parsePercentDiff(data.donationsPercentageDiff, 0),
    trendData: {
      inflows: [previousMonthFunds, currentMonthFunds],
      outflows: [previousMonthExpenses, currentMonthExpenses],
    },
    currentMonthFunds,
    previousMonthFunds,
    currentMonthExpenses,
    previousMonthExpenses,
    fundsPercentChange,
    expensesPercentChange,
    raw: data,
  };
};

export const calculateFinancialSummary = (expenses = [], funds = []) => {
  const safeExpenses = Array.isArray(expenses) ? expenses : [];
  const safeFunds = Array.isArray(funds) ? funds : [];
  
  const totalOutflows = safeExpenses.reduce((sum, expense) => {
    // Only use grandTotal if available, otherwise fall back to amount
    const value = expense.grandTotal ? Number(expense.grandTotal) : Number(expense.amount) || 0;
    return sum + value;
  }, 0);
  
  // Calculate total inflows (funds)
  const totalInflows = safeFunds.reduce((sum, fund) => 
    sum + (Number(fund.amount) || 0), 0);
    
  // Calculate current balance
  const currentBalance = totalInflows - totalOutflows;
  
  const trendData = calculateTrendData(safeExpenses, safeFunds);
  
  const monthlySummary = calculateMonthlySummary(safeExpenses, safeFunds);
  
  return {
    totalOutflows,
    totalInflows,
    currentBalance,
    trendData,
    monthlySummary
  };
};

export const DUMMY_DONATIONS_AMOUNT = 0;